import { Button, Loader } from '@southfields-digital/mpxlive-components';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getRepeaterItems } from 'src/components/GraphicPropertiesForm';
import Repeater from 'src/components/Repeater';
import { Option } from 'src/components/Select';
import { StateType } from 'src/redux/reducers';
import { REQUEST_GET_SPORT_DATA_HOCKEY_STANDINGS } from 'src/redux/reducers/sportData';

import SportDataSelect from '../SportDataSelect';

import styles from './HockeyStandings.module.scss';

type Props = {
  handleImportData: (values: PropertyValue[]) => void;
  selectedProperty: RepeaterProperties;
};

const MATCH_OPTIONS: Option[] = [
  { label: 'Select match..', value: '' },

  // VROUWEN (start at 20513)
  { label: 'W01: NED v IRL (Pool A)', value: '20513' },
  { label: 'W02: GER v FRA (Pool A)', value: '20514' },
  { label: 'W03: ENG v SCO (Pool B)', value: '20515' },
  { label: 'W04: BEL v ESP (Pool B)', value: '20516' },
  { label: 'W05: ESP v SCO (Pool B)', value: '20517' },
  { label: 'W06: IRL v FRA (Pool A)', value: '20518' },
  { label: 'W07: ENG v BEL (Pool B)', value: '20519' },
  { label: 'W08: GER v NED (Pool A)', value: '20520' },
  { label: 'W09: ESP v ENG (Pool B)', value: '20521' },
  { label: 'W10: BEL v SCO (Pool B)', value: '20522' },
  { label: 'W11: NED v FRA (Pool A)', value: '20523' },
  { label: 'W12: IRL v GER (Pool A)', value: '20524' },
  { label: 'W13: 4th Pool A v 4th Pool B (Pool C)', value: '20525' },
  { label: 'W14: 3rd Pool A v 3rd Pool B (Pool C)', value: '20526' },
  { label: 'W15: 1st Pool A v 2nd Pool B (Semi-final 1)', value: '20527' },
  { label: 'W16: 1st Pool B v 2nd Pool A (Semi-final 2)', value: '20528' },
  { label: 'W17: 3rd Pool B v 4th Pool A (Pool C)', value: '20529' },
  { label: 'W18: 3rd Pool A v 4th Pool B (Pool C)', value: '20530' },
  { label: 'W19: Loser 15 v Loser 16 (3rd/4th Place)', value: '20531' },
  { label: 'W20: Winner 15 v Winner 16 (Final)', value: '20532' },

  // MANNEN (start at 20493)
  { label: 'M01: ENG v POL (Pool B)', value: '20493' },
  { label: 'M02: GER v FRA (Pool B)', value: '20494' },
  { label: 'M03: BEL v AUT (Pool A)', value: '20495' },
  { label: 'M04: NED v ESP (Pool A)', value: '20496' },
  { label: 'M05: FRA v POL (Pool B)', value: '20497' },
  { label: 'M06: ENG v GER (Pool B)', value: '20498' },
  { label: 'M07: BEL v NED (Pool A)', value: '20499' },
  { label: 'M08: ESP v AUT (Pool A)', value: '20500' },
  { label: 'M09: FRA v ENG (Pool B)', value: '20501' },
  { label: 'M10: ESP v BEL (Pool A)', value: '20502' },
  { label: 'M11: NED v AUT (Pool A)', value: '20503' },
  { label: 'M12: GER v POL (Pool B)', value: '20504' },
  { label: 'M13: 4th Pool A v 4th Pool B (Pool C)', value: '20505' },
  { label: 'M14: 3rd Pool A v 3rd Pool B (Pool C)', value: '20506' },
  { label: 'M15: 1st Pool A v 2nd Pool B (Semi-final 1)', value: '20507' },
  { label: 'M16: 1st Pool B v 2nd Pool A (Semi-final 2)', value: '20508' },
  { label: 'M17: 3rd Pool A v 4th Pool B (Pool C)', value: '20509' },
  { label: 'M18: 3rd Pool B v 4th Pool A (Pool C)', value: '20510' },
  { label: 'M19: Loser 15 v Loser 16 (3rd/4th Place)', value: '20511' },
  { label: 'M20: Winner 15 v Winner 16 (Final)', value: '20512' },
];

export default function HockeyStandings({ handleImportData, selectedProperty }: Props) {
  const dispatch = useDispatch();
  const { hockeyStandings } = useSelector((state: StateType) => state.sportData);

  const [matchId, setMatchId] = useState<string>(hockeyStandings.data.matchId || '');

  const propertyValues = useMemo<PropertyValue[]>(
    () =>
      hockeyStandings.data.items
        ?.map(({ statsType, statsValueHome, statsValueAway }) => ({
          statsType,
          statsValueHome,
          statsValueAway,
        }))
        .reverse() || [],
    [hockeyStandings.data.items]
  );

  const repeaterValues = useMemo(
    () => (propertyValues.length ? getRepeaterItems(selectedProperty, propertyValues) : []),
    [propertyValues, selectedProperty]
  );

  useEffect(() => {
    if (!matchId) return;
    dispatch({
      type: REQUEST_GET_SPORT_DATA_HOCKEY_STANDINGS,
      payload: { matchId },
    });
  }, [matchId, dispatch]);

  const handleChangeMatch = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setMatchId(e.currentTarget.value);
  }, []);

  const handleClickImport = useCallback(() => {
    handleImportData(propertyValues);
  }, [handleImportData, propertyValues]);

  const renderSelect = (
    <div className={styles.Match}>
      <SportDataSelect
        label="Selected match"
        value={matchId}
        options={MATCH_OPTIONS}
        onChange={handleChangeMatch}
      />
    </div>
  );

  if (hockeyStandings.loading || !hockeyStandings.data.items?.length) {
    return (
      <div className={styles.Container}>
        <div className={styles.Header} />
        <div className={styles.Content}>
          <div className={styles.FilterContainer}>{renderSelect}</div>
          {hockeyStandings.loading && (
            <div className={styles.LoadingContainer}>
              <Loader className={styles.Loading} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        <Button className={styles.ImportButton} onClick={handleClickImport}>
          Import
        </Button>
      </div>
      <div className={styles.Content}>
        <div className={styles.FilterContainer}>{renderSelect}</div>
        <Repeater
          id=""
          onChangeRows={() => {}}
          items={repeaterValues}
          isDisabled
          excludedIds={['divider', 'highlight']}
        />
      </div>
    </div>
  );
}
